import { useRef, useState } from 'react'
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Button, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import { AttachmentRow } from './AttachmentRow'
import { MAX_ATTACHMENTS, type AttachmentItem } from '../lib/attachments.types'

interface AttachmentDropZoneProps {
  items: AttachmentItem[]
  onAdd: (files: File[]) => void
  onRetry: (id: string) => void
  onCancel: (id: string) => void
  onRemove: (id: string) => void
  disabled?: boolean
  isFull?: boolean
  title?: string
  subtitle?: string
  forceDragActive?: boolean
}

export const AttachmentDropZone = ({
  items,
  onAdd,
  onRetry,
  onCancel,
  onRemove,
  disabled = false,
  isFull = false,
  title = 'Drop documents here',
  subtitle = 'Goes to support with the ticket',
  forceDragActive = false,
}: AttachmentDropZoneProps) => {
  const [isDragActive, setIsDragActive] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const dragActive = forceDragActive || isDragActive

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (disabled) return
    setIsDragActive(true)
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    if (disabled) return
    setIsDragActive(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragActive(false)
    if (disabled || isFull) return
    onAdd(Array.from(e.dataTransfer.files))
  }

  const handleBrowseClick = () => {
    inputRef.current?.click()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onAdd(Array.from(e.target.files))
    }
    e.target.value = ''
  }

  return (
    <div className="flex flex-col gap-3">
      <div
        data-testid="attachment-drop-zone"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={mtc(
          'flex items-center gap-3 rounded-lg',
          'border border-dashed px-4 py-3',
          dragActive ? 'border-moss-200 bg-moss-25' : 'border-neutral-200',
          disabled && 'opacity-50'
        )}
      >
        <Icon icon={faCloudArrowUp} color="text-neutral-400" />

        {isFull ? (
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {`You can attach up to ${MAX_ATTACHMENTS} files.`}
          </Text>
        ) : (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <Text
                variant={TypographyStyles.LABEL_SM}
                color={TextColors.HEADER}
              >
                {title}
              </Text>
              <Text variant={TypographyStyles.BODY_XS} color={TextColors.MUTED}>
                {subtitle}
              </Text>
            </div>
            <Button
              variant="secondary"
              size="xs"
              onClick={handleBrowseClick}
              disabled={disabled}
            >
              Browse
            </Button>
            <input
              ref={inputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleInputChange}
              disabled={disabled}
            />
          </>
        )}
      </div>

      {items.length > 0 && (
        <div className="flex flex-col gap-2">
          {items.map((item) => (
            <AttachmentRow
              key={item.id}
              item={item}
              onRetry={onRetry}
              onCancel={onCancel}
              onRemove={onRemove}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  )
}
